import type { QueryClient, UseQueryResult } from '@tanstack/react-query';
import { useQuery } from '@tanstack/react-query';
import type { EntityPage, EntityPageOwner } from '@trinity/chat/types/entity-pages';
import { useChatApiClient } from '../containers/Chat/useApiClient';

/**
 * Query keys for a project's / brand's tabs and attached pages (the tabs include the pages, so they're
 * invalidated together); invalidate `all(owner)` after a change.
 */
export const entityPageQueryKeys = {
  all: (owner: EntityPageOwner) => ['entityPages', owner.type, owner.id] as const,
  tabs: (owner: EntityPageOwner) => ['entityPages', owner.type, owner.id, 'tabs'] as const,
  page: (owner: EntityPageOwner, pageIdentifier: string) =>
    ['entityPages', owner.type, owner.id, 'page', pageIdentifier] as const,
};

/** Refetches a project's tabs, e.g. after its quant analyses change (each is a tab). */
export function invalidateProjectTabs(queryClient: QueryClient, projectId: number): Promise<void> {
  return queryClient.invalidateQueries({
    queryKey: entityPageQueryKeys.tabs({ type: 'project', id: projectId }),
  });
}

/** A page attached to a project or brand. */
export function useEntityPage(
  owner: EntityPageOwner | null,
  pageIdentifier: string | null
): UseQueryResult<EntityPage> {
  const apiClient = useChatApiClient();

  return useQuery({
    queryKey: entityPageQueryKeys.page(owner ?? { type: 'project', id: 0 }, pageIdentifier ?? ''),
    queryFn: async () => {
      if (owner === null || pageIdentifier === null) {
        throw new Error('owner and pageIdentifier are required to fetch a page');
      }

      return apiClient.entityPages.getPage(owner, pageIdentifier);
    },
    enabled: owner !== null && pageIdentifier !== null,
  });
}
